import { useState } from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import type { CoverageBreakdown, IndicationOutput } from '@zg/shared';
import { formatCurrency } from '../../lib/format';
import { useReducedMotion } from '../../hooks/useReducedMotion';

export type CoverageKey = 'builderRisk' | 'oiGl' | 'oiExcess' | 'pollution';

interface CoverageRow {
  key: CoverageKey;
  label: string;
  code: string;
  description: string;
}

const ROWS: CoverageRow[] = [
  {
    key: 'builderRisk',
    label: 'Builders Risk',
    code: 'BR_AOP',
    description: 'Covers the project under construction. Materials, labor, and work-in-progress.',
  },
  {
    key: 'oiGl',
    label: 'OI General Liability',
    code: 'OI_GL',
    description: 'Owner’s interest liability for bodily injury and property damage.',
  },
  {
    key: 'oiExcess',
    label: 'OI Excess $5M',
    code: 'OI_EXCESS',
    description: 'Excess liability above the primary GL limits.',
  },
  {
    key: 'pollution',
    label: 'Pollution Liability',
    code: 'POLLUTION',
    description: 'Coverage for pollution conditions arising from the project.',
  },
];

export interface CoverageAccordionProps {
  output: IndicationOutput;
}

function BreakdownTable({
  best,
  average,
  conservative,
}: {
  best: CoverageBreakdown;
  average: CoverageBreakdown;
  conservative: CoverageBreakdown;
}) {
  const rows: { label: string; key: keyof CoverageBreakdown }[] = [
    { label: 'Term Premium', key: 'termPremium' },
    { label: 'TRIA', key: 'tria' },
    { label: 'Taxes & Fees', key: 'taxesFees' },
    { label: 'Total', key: 'total' },
  ];
  return (
    <div className="mt-5 border-t border-hairline pt-5">
      <div className="grid grid-cols-4 gap-4 font-mono text-[10px] uppercase tracking-[0.125em] text-ink-subtle">
        <div>&gt; LINE</div>
        <div className="text-right">BEST</div>
        <div className="text-right">AVERAGE</div>
        <div className="text-right">CONSERVATIVE</div>
      </div>
      <dl className="mt-3 divide-y divide-hairline">
        {rows.map((r) => {
          const isTotal = r.key === 'total';
          return (
            <div
              key={r.key}
              className={clsx(
                'grid grid-cols-4 gap-4 py-2.5 font-tnum text-[0.875rem]',
                isTotal ? 'text-ink font-medium' : 'text-ink-muted',
              )}
            >
              <dt>{r.label}</dt>
              <dd className="text-right">{formatCurrency(best[r.key])}</dd>
              <dd className="text-right">{formatCurrency(average[r.key])}</dd>
              <dd className="text-right">{formatCurrency(conservative[r.key])}</dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

export function CoverageAccordion({ output }: CoverageAccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const reduced = useReducedMotion() ?? false;

  return (
    <ul className="flex flex-col gap-3">
      {ROWS.map((row, i) => {
        const open = openIndex === i;
        const best = output.scenarios.best[row.key];
        const avg = output.scenarios.average[row.key];
        const cons = output.scenarios.conservative[row.key];
        const panelId = `coverage-panel-${row.key}`;
        return (
          <li key={row.key} className="bg-panel border border-hairline rounded-md overflow-hidden">
            <button
              type="button"
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => setOpenIndex(open ? null : i)}
              className="w-full flex items-center justify-between gap-6 p-5 text-left hover:bg-canvas/60 transition-colors"
            >
              <span className="flex-1 min-w-0">
                <span className="block font-mono text-[10px] uppercase tracking-[0.125em] text-ink-subtle">
                  &gt; {row.code}
                </span>
                <span className="mt-1 block font-display font-medium text-[1.0625rem] text-ink">
                  {row.label}
                </span>
                <span className="mt-1 block text-[0.8125rem] text-ink-muted">
                  {row.description}
                </span>
              </span>
              <span className="flex items-center gap-4 shrink-0">
                <span className="font-display font-tnum text-[0.9375rem] text-ink whitespace-nowrap">
                  {formatCurrency(best.total)}&nbsp;to&nbsp;{formatCurrency(cons.total)}
                </span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  className={clsx(
                    'transition-transform duration-300 text-ink-subtle',
                    open && 'rotate-180',
                  )}
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </button>
            <AnimatePresence initial={false}>
              {open && (
                <motion.div
                  id={panelId}
                  key="panel"
                  initial={{ height: reduced ? 'auto' : 0, opacity: reduced ? 1 : 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: reduced ? 'auto' : 0, opacity: reduced ? 1 : 0 }}
                  transition={{ duration: reduced ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
                  style={{ overflow: 'hidden' }}
                >
                  <div className="px-5 pb-5">
                    <BreakdownTable best={best} average={avg} conservative={cons} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </li>
        );
      })}
    </ul>
  );
}
